/**
 * Lightweight CSS rotating globe, a brand-styled equirectangular world map
 * (golden land, blue ocean, Pakistan green) scrolled horizontally inside a
 * circular mask to mimic a spinning Earth, with radial shading for the sphere
 * illusion and a soft atmosphere glow.
 *
 * Deliberately NOT a WebGL/three.js globe: at logo size that's overkill and its
 * multi-second init forced an ugly placeholder. This renders instantly (one 66KB
 * image), animates entirely on the GPU (no main-thread jank), and has no load
 * gap, so there is no "blue bulb" before it appears.
 */

const TEXTURE = "/images/globe-texture.png";

export function StylizedGlobe({
  className = "",
  spinDegPerSec = 60,
  axisTiltDeg = 23.5,
}: {
  className?: string;
  /** Spin rate in degrees per second (360 / value = seconds per full turn). */
  spinDegPerSec?: number;
  /** Earth's axial tilt, leans the globe east (client requirement). */
  axisTiltDeg?: number;
}) {
  const secondsPerTurn = Math.max(1, 360 / spinDegPerSec);

  return (
    <div
      className={`stylized-globe relative ${className}`}
      aria-hidden="true"
      style={{ transform: `rotate(${axisTiltDeg}deg)` }}
    >
      {/* Circular sphere: clips the scrolling map and carries the atmosphere glow. */}
      <div
        className="absolute inset-0 overflow-hidden rounded-full"
        style={{
          backgroundColor: "#2767c4",
          boxShadow: "0 0 9px 1px rgba(143,184,236,0.4)",
        }}
      >
        {/* Two side-by-side copies of the map; translating by -50% loops seamlessly. */}
        <div
          className="absolute inset-y-0 left-0 flex h-full"
          style={{
            width: "max-content",
            // reverse: map now scrolls west-to-east instead of east-to-west
            animation: `pbd-globe-spin ${secondsPerTurn}s linear infinite reverse`,
            willChange: "transform",
          }}
        >
          <img
            src={TEXTURE}
            alt=""
            width={2048}
            height={1024}
            className="h-full w-auto max-w-none select-none"
            draggable={false}
          />
          <img
            src={TEXTURE}
            alt=""
            width={2048}
            height={1024}
            className="h-full w-auto max-w-none select-none"
            draggable={false}
          />
        </div>

        {/* Sphere shading: top-left highlight, bottom-right shadow. */}
        <div
          className="pointer-events-none absolute inset-0 rounded-full"
          style={{
            background:
              "radial-gradient(circle at 34% 28%, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0) 42%), radial-gradient(circle at 68% 74%, rgba(8,24,52,0.5) 0%, rgba(8,24,52,0) 60%)",
          }}
        />
      </div>
    </div>
  );
}
